import { useState } from 'react';

import {
  Alert,
  Button,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';

import { reloadClasses } from '../../../api/Put';

const { Paragraph, Text, Title } = Typography;

const PLATFORMS = [
  { label: 'Saas', value: 'prod-saas' },
  { label: 'Mutu', value: 'prod-mutu' },
  { label: 'Réunion', value: 'prod-reunion' },
  { label: 'HDF', value: 'prod-hdf' },
  { label: 'Nouvelle-Calédonie', value: 'prod-nc' },
  { label: 'SYN', value: 'prod-syn' },
  { label: 'Essonne', value: 'prod-essonne' },
  { label: 'Normandie', value: 'prod-normandie' },
  { label: 'PCN', value: 'prod-pcn' },
  { label: 'Polynésie', value: 'prod-polynesie' },
  { label: 'Martinique', value: 'prod-martinique' },
];

const CLASS_COLUMNS = [
  {
    dataIndex: 'nom',
    key: 'nom',
    render: (nom, row) => (
      <Space direction="vertical" size={0}>
        <Text strong>{nom}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {row.id}
        </Text>
      </Space>
    ),
    title: 'Classe',
  },
  { dataIndex: 'eleves', key: 'eleves', title: 'Effectif Edifice' },
  {
    dataIndex: 'ajoutes',
    key: 'ajoutes',
    render: n => (n > 0 ? <Tag color="green">+{n}</Tag> : <Text type="secondary">—</Text>),
    title: 'Ajoutés',
  },
  {
    dataIndex: 'deplaces',
    key: 'deplaces',
    render: n => (n > 0 ? <Tag color="blue">{n}</Tag> : <Text type="secondary">—</Text>),
    title: 'Changement de classe',
  },
  {
    dataIndex: 'retires',
    key: 'retires',
    render: n => (n > 0 ? <Tag color="red">-{n}</Tag> : <Text type="secondary">—</Text>),
    title: 'Retirés',
  },
  { dataIndex: 'inchanges', key: 'inchanges', title: 'Inchangés' },
];

const REMOVED_COLUMNS = [
  { dataIndex: 'idEdifice', key: 'idEdifice', title: 'Identifiant' },
  {
    key: 'nom',
    render: (_, row) => `${row.firstName || ''} ${row.lastName || ''}`.trim() || '—',
    title: 'Élève',
  },
  { dataIndex: 'idClasse', key: 'idClasse', title: 'Classe quittée' },
];

/*
 * Outil de support : recharger les classes d'un enseignant et voir ce que cela a changé.
 * Sans ce détail, on ne peut pas distinguer « rien à faire » de « tout a été vidé ».
 */
const ReloadClasses = () => {
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const handleReloadClasses = async values => {
    setError('');
    setResult(null);
    setLoading(true);

    const res = await reloadClasses(values);
    setLoading(false);

    if (res.error) setError(res.error);
    else {
      setResult(res);
      messageApi.open({ content: `Classes de l'enseignant ${values.idUser} rechargées`, type: 'success' });
    }
  };

  const rienChange = result && result.effectifs.ajoutes === 0 && result.effectifs.deplaces === 0 && result.effectifs.retires === 0;

  return (
    <>
      {contextHolder}
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space
          direction="vertical"
          style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 5, padding: 15, width: '100%' }}>
          <Title level={4} style={{ marginBottom: 0 }}>
            Recharger les classes d&apos;un enseignant
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Interroge Edifice et réaligne les classes, les effectifs et les parents de cet enseignant. Les élèves qui ont quitté une
            classe en sont retirés, ceux qui l&apos;ont rejointe y sont ajoutés. Une classe dont l&apos;appel Edifice échoue
            n&apos;est jamais vidée.
          </Paragraph>

          <Form name="reload" initialValues={{ idProject: 'prod-saas' }} onFinish={handleReloadClasses} layout="vertical">
            <Row gutter={16}>
              <Col>
                <Form.Item label="ID utilisateur" name="idUser" rules={[{ message: 'Identifiant requis', required: true }]}>
                  <Input placeholder="0ebb28b9-5df0-4485-a13f-5644f2550952" style={{ width: 340 }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Plateforme" name="idProject" rules={[{ message: 'Plateforme requise', required: true }]}>
                  <Select options={PLATFORMS} showSearch />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={loading}>
                Recharger
              </Button>
            </Form.Item>
          </Form>
        </Space>

        {error && <Alert message={error} showIcon type="error" onClose={() => setError('')} closable />}

        {loading && <Alert type="info" showIcon message="Interrogation d’Edifice en cours…" />}

        {result && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type={rienChange ? 'info' : 'success'}
              showIcon
              message={rienChange ? 'Rechargement effectué — aucun changement d’effectif' : 'Rechargement effectué'}
              description={`${result.classes.rattachees} classe(s) rattachée(s)${
                result.classes.detachees > 0 ? `, ${result.classes.detachees} détachée(s)` : ''
              } · ${Math.round(result.durationMs / 1000)} s`}
            />

            <Row gutter={16}>
              <Col>
                <Statistic title="Élèves vus chez Edifice" value={result.effectifs.vusChezEdifice} />
              </Col>
              <Col>
                <Statistic title="Ajoutés" value={result.effectifs.ajoutes} valueStyle={{ color: '#3f8600' }} />
              </Col>
              <Col>
                <Statistic title="Changement de classe" value={result.effectifs.deplaces} valueStyle={{ color: '#1677ff' }} />
              </Col>
              <Col>
                <Statistic title="Retirés" value={result.effectifs.retires} valueStyle={{ color: '#cf1322' }} />
              </Col>
              <Col>
                <Statistic title="Inchangés" value={result.effectifs.inchanges} />
              </Col>
            </Row>

            <Descriptions bordered column={2} size="small" title="Avant / après">
              <Descriptions.Item label="Classes avant">{result.avant.classes}</Descriptions.Item>
              <Descriptions.Item label="Classes après">{result.apres.classes}</Descriptions.Item>
              <Descriptions.Item label="Élèves avant">{result.avant.eleves}</Descriptions.Item>
              <Descriptions.Item label="Élèves après">{result.apres.eleves}</Descriptions.Item>
            </Descriptions>

            {result.classes.enErreur > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${result.classes.enErreur} classe(s) n’ont pas pu être interrogées`}
                description="Leurs effectifs n’ont pas été touchés : aucune classe n’est vidée sur un appel en échec. Relancer plus tard."
              />
            )}

            <div>
              <Title level={5}>Détail par classe</Title>
              {result.detail.length > 0 ? (
                <Table columns={CLASS_COLUMNS} dataSource={result.detail} pagination={false} rowKey="id" size="small" />
              ) : (
                <Empty description="Aucune classe rechargée" />
              )}
            </div>

            {result.elevesRetires.length > 0 && (
              <div>
                <Title level={5}>Élèves retirés</Title>
                <Paragraph type="secondary">
                  Ces élèves ne figurent plus dans les effectifs renvoyés par Edifice pour les classes rechargées.
                </Paragraph>
                <Table
                  columns={REMOVED_COLUMNS}
                  dataSource={result.elevesRetires}
                  pagination={{ pageSize: 10 }}
                  rowKey="idEdifice"
                  size="small"
                />
              </div>
            )}

            {result.erreurs.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${result.erreurs.length} erreur(s)`}
                description={
                  <ul style={{ marginBottom: 0 }}>
                    {result.erreurs.map(e => (
                      <li key={e.idEdifice || e.id}>
                        {e.idEdifice || e.id} : {e.message || e.status}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
          </Space>
        )}
      </Space>
    </>
  );
};

export default ReloadClasses;
